import { motion } from 'motion/react';
import { memo, useCallback, useMemo } from 'react';

import { type WorkspaceAgentPanelZeroStateChip } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelZeroStateSuggestions';
import {
  WORKSPACE_AGENT_ZERO_STATE_ENTRANCE_STAGGER_S,
  WORKSPACE_AGENT_ZERO_STATE_ENTRANCE_TRANSLATE_Y_PX,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentZeroStateEntrance';
import {
  WorkspaceAgentQuickReplyChip,
  type WorkspaceAgentQuickReplyChipVariant,
} from '~/components/v2/components/workspace/workspace-agent-panel/design-system/chips/WorkspaceAgentQuickReplyChip';
import { WorkspaceAgentQuickReplyChipShimmer } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/chips/WorkspaceAgentQuickReplyChipShimmer';

export type WorkspaceAgentPanelZeroStateSuggestionRowProps = {
  index: number;
  isDisabled: boolean;
  isVisible: boolean;
  onSelect: (id: string) => void;
  showQuickReplyShimmer: boolean;
  /** How many chips share the shimmer sweep, so it reaches every row once per cycle. */
  slotCount: number;
  suggestion: WorkspaceAgentPanelZeroStateChip;
  transitionDuration: number;
  variant?: WorkspaceAgentQuickReplyChipVariant;
};

export const WorkspaceAgentPanelZeroStateSuggestionRow = memo(
  ({
    index,
    isDisabled,
    isVisible,
    onSelect,
    showQuickReplyShimmer,
    slotCount,
    suggestion,
    transitionDuration,
    variant = 'outlined',
  }: WorkspaceAgentPanelZeroStateSuggestionRowProps) => {
    const handleSelect = useCallback(() => onSelect(suggestion.id), [onSelect, suggestion.id]);

    const chip = useMemo(
      () => (
        <WorkspaceAgentQuickReplyChip
          data-testid="WORKSPACE_AGENT_PANEL_ZERO_STATE_SUGGESTION"
          icon={suggestion.icon}
          isBorderless={showQuickReplyShimmer}
          isDisabled={isDisabled}
          label={suggestion.title}
          onSelect={handleSelect}
          suggestionId={suggestion.id}
          variant={variant}
        />
      ),
      [handleSelect, isDisabled, showQuickReplyShimmer, suggestion.icon, suggestion.id, suggestion.title, variant],
    );

    const chipFrame = useMemo(() => {
      if (!showQuickReplyShimmer) {
        return chip;
      }

      return (
        <WorkspaceAgentQuickReplyChipShimmer
          className={variant === 'ghost' ? 'inline-block max-w-full' : undefined}
          index={index}
          isRounded={variant === 'ghost'}
          slotCount={slotCount}
          suggestionId={suggestion.id}
        >
          {chip}
        </WorkspaceAgentQuickReplyChipShimmer>
      );
    }, [chip, index, showQuickReplyShimmer, slotCount, suggestion.id, variant]);

    return (
      <motion.div
        animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : WORKSPACE_AGENT_ZERO_STATE_ENTRANCE_TRANSLATE_Y_PX }}
        className={variant === 'ghost' ? 'min-w-0 max-w-full' : undefined}
        initial={{ opacity: 0, y: WORKSPACE_AGENT_ZERO_STATE_ENTRANCE_TRANSLATE_Y_PX }}
        transition={{
          delay: index * WORKSPACE_AGENT_ZERO_STATE_ENTRANCE_STAGGER_S,
          duration: transitionDuration,
          ease: 'easeOut',
        }}
      >
        {chipFrame}
      </motion.div>
    );
  },
);

WorkspaceAgentPanelZeroStateSuggestionRow.displayName = 'WorkspaceAgentPanelZeroStateSuggestionRow';
